import {
  type ChangeEvent,
  type ReactNode,
  useCallback,
  useId,
  useState,
} from 'react';
import type { Components } from 'react-markdown';

import {
  RiArrowRightLine,
  RiGitClosePullRequestLine,
  RiGitMergeLine,
  RiGitPrDraftLine,
  RiGitPullRequestLine,
} from '@remixicon/react';
import classNames from 'classnames';

import {
  Box,
  Button,
  Flex,
  FormGroup,
  FormRadio,
  FormTextarea,
  Tag,
  type TagProps,
  Text,
} from '@spotify-internal/encore-web';
import { ListRow } from '@spotify-internal/encore-web/next';
import {
  Copy,
  DateUtils,
  emojify,
  useDocumentVisibilityDetection,
} from '@spotify/confidence-core-react';
import {
  getError,
  getTypeOrNull,
  usePullRequestQuery,
} from '@spotify/confidence-graphql';
import { MarkdownContent } from '@spotify/plugin-confidence-chat-react';
import {
  TaskFragment,
  type TaskInputResponseInput,
  TaskState,
  useTaskSuspense,
} from '@spotify/plugin-confidence-issues-react';

import { useProvideIssueTaskInput } from '../../hooks';
import { ExpandableContent } from '../ExpandableContent';
import {
  CodeReviewXirpForSpotifyAction,
  CodingSessionLaunchFallback,
} from '../IssueCodingSessionAction';
import { TaskLatestChatEvent } from './TaskLatestChatEvent';
import { TaskStateIcon } from './TaskStateIcon';

import styles from './TaskCard.module.css';

export type TaskCardProps = {
  canEdit: boolean;
  isHighlighted?: boolean;
  taskName: string;
  issueName: string;
};

export type TaskCardFallbackProps = {
  canEdit: boolean;
  isHighlighted?: boolean;
  task: TaskFragment;
  issueName: string;
};

type TaskInputRequest = NonNullable<TaskFragment['inputRequest']>;
type TaskInputResponse = NonNullable<TaskFragment['inputResponse']>;
type CodeReviewInputRequest = NonNullable<TaskInputRequest['codeReview']>;

const OTHER_DIRECTION_OPTION = 'other';
const CHANGE_STAT_BLOCK_COUNT = 5;
const CHANGE_STAT_BLOCK_SIZE = 5;
const PULL_REQUEST_POLL_INTERVAL_MS = 30 * 1000;

type TaskPlanHeadingElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

const TaskPlanHeading = ({
  as,
  children,
}: {
  as: TaskPlanHeadingElement;
  children?: ReactNode;
}) => {
  const Heading = as;

  return <Heading className={styles.taskPlanHeading}>{children}</Heading>;
};

const taskPlanMarkdownComponents: Components = {
  h1: props => <TaskPlanHeading {...props} as="h1" />,
  h2: props => <TaskPlanHeading {...props} as="h2" />,
  h3: props => <TaskPlanHeading {...props} as="h3" />,
  h4: props => <TaskPlanHeading {...props} as="h4" />,
  h5: props => <TaskPlanHeading {...props} as="h5" />,
  h6: props => <TaskPlanHeading {...props} as="h6" />,
};

const getTaskInputResponseLabel = (
  inputResponse: TaskInputResponse,
): string | undefined => {
  if (inputResponse.approval) {
    const approvalLabel = inputResponse.approval.approved
      ? 'Approved'
      : 'Rejected';

    return inputResponse.approval.comment
      ? `${approvalLabel}: ${inputResponse.approval.comment}`
      : approvalLabel;
  }

  if (inputResponse.codeReview) {
    const codeReviewLabel = inputResponse.codeReview.approved
      ? 'Approved'
      : 'Rejected';

    return inputResponse.codeReview.comment
      ? `${codeReviewLabel}: ${inputResponse.codeReview.comment}`
      : codeReviewLabel;
  }

  if (inputResponse.direction) {
    return inputResponse.direction.choice;
  }

  return undefined;
};

const buildReviewInputResponse = (
  inputType: 'approval' | 'codeReview',
  approved: boolean,
  comment: string,
): TaskInputResponseInput => {
  const trimmedComment = comment.trim();

  return {
    [inputType]: {
      approved,
      ...(trimmedComment && { comment: trimmedComment }),
    },
  };
};

const PullRequestPreview = ({
  codeReview,
}: {
  codeReview: CodeReviewInputRequest;
}) => {
  const { data, error, loading, refetch } = usePullRequestQuery({
    variables: {
      repository: codeReview.repository,
      number: codeReview.pullRequestNumber,
    },
    fetchPolicy: 'cache-and-network',
    pollInterval: PULL_REQUEST_POLL_INTERVAL_MS,
  });
  const handleVisibilityChange = useCallback(
    (isDocumentVisible: boolean) => {
      if (isDocumentVisible) {
        void refetch();
      }
    },
    [refetch],
  );
  useDocumentVisibilityDetection(handleVisibilityChange);
  const pullRequest = getTypeOrNull(data?.pullRequest);
  const pullRequestError = error ?? getError(data?.pullRequest);

  if (loading && !data) {
    return (
      <Text as="p" variant="bodySmall" semanticColor="textSubdued">
        Loading pull request
      </Text>
    );
  }

  if (pullRequestError || !pullRequest) {
    return (
      <Text as="p" variant="bodySmall" semanticColor="textNegative">
        Unable to load pull request.
      </Text>
    );
  }

  const pullRequestNumber = pullRequest.number ?? codeReview.pullRequestNumber;
  const pullRequestUrl = pullRequest.htmlUrl ?? codeReview.uri;
  const additions = pullRequest.additions ?? 0;
  const deletions = pullRequest.deletions ?? 0;
  const changeCount = additions + deletions;
  const additionBlockCount = Math.min(
    Math.ceil(additions / CHANGE_STAT_BLOCK_SIZE),
    CHANGE_STAT_BLOCK_COUNT,
  );
  const deletionBlockCount = Math.min(
    Math.ceil(deletions / CHANGE_STAT_BLOCK_SIZE),
    CHANGE_STAT_BLOCK_COUNT - additionBlockCount,
  );
  const emptyBlockCount =
    CHANGE_STAT_BLOCK_COUNT - additionBlockCount - deletionBlockCount;
  const pullRequestMetadata = pullRequest.createdAt
    ? `#${pullRequestNumber} opened ${DateUtils.xAgo(
        new Date(pullRequest.createdAt),
      )}${pullRequest.userLogin ? ` by ${pullRequest.userLogin}` : ''}`
    : `#${pullRequestNumber}`;
  let PullRequestIcon = RiGitPullRequestLine;
  let pullRequestIconClass = styles.pullRequestIconOpen;
  let pullRequestStatus: string | undefined;
  let statusColorSet: TagProps['colorSet'] | undefined;

  if (pullRequest.merged) {
    PullRequestIcon = RiGitMergeLine;
    pullRequestIconClass = styles.pullRequestIconMerged;
    pullRequestStatus = 'Merged';
    statusColorSet = 'announcement';
  } else if (pullRequest.state === 'closed') {
    PullRequestIcon = RiGitClosePullRequestLine;
    pullRequestIconClass = styles.pullRequestIconClosed;
    pullRequestStatus = 'Closed';
    statusColorSet = 'negative';
  } else if (pullRequest.draft) {
    PullRequestIcon = RiGitPrDraftLine;
    pullRequestIconClass = styles.pullRequestIconDraft;
    pullRequestStatus = 'Draft';
  } else if (pullRequest.mergeable === false) {
    pullRequestStatus = 'Not mergeable';
    statusColorSet = 'negative';
  } else {
    pullRequestStatus = 'Open';
    statusColorSet = 'positive';
  }

  const reviewersLabel =
    pullRequest.requestedReviewers.length > 0
      ? `${pullRequest.requestedReviewers.length} reviewer${
          pullRequest.requestedReviewers.length === 1 ? '' : 's'
        } requested`
      : undefined;

  return (
    <Box
      variant="tinted"
      padding="base"
      className={styles.pullRequestPreview}
      href={pullRequestUrl}
      target="_blank"
      rel="noopener noreferrer"
     data-track-source-anchor="9010186da6;pull-request-preview-box">
      <div className={styles.pullRequestLayout}>
        <div
          className={classNames(styles.pullRequestIcon, pullRequestIconClass)}
        >
          <PullRequestIcon
            size="var(--encore-graphic-size-decorative-larger)"
            role="img"
            aria-label="Pull request"
          />
        </div>
        <div className={styles.pullRequestContent}>
          <div className={styles.pullRequestHeader}>
            <div>
              <Text as="span" variant="bodySmallBold">
                {pullRequest.title
                  ? emojify(pullRequest.title)
                  : `Pull request #${pullRequestNumber}`}
              </Text>
              <Text as="p" variant="bodySmall" semanticColor="textSubdued">
                {pullRequestMetadata}
              </Text>
            </div>
            <div className={styles.pullRequestHeaderActions}>
              {changeCount > 0 && (
                <div className={styles.changeStats}>
                  <Text
                    as="span"
                    variant="marginal"
                    className={styles.additionsLabel}
                  >
                    {`+${additions}`}
                  </Text>
                  <Text
                    as="span"
                    variant="marginal"
                    className={styles.deletionsLabel}
                  >
                    {`−${deletions}`}
                  </Text>
                  <div
                    className={styles.changeStatsBlocks}
                    role="img"
                    aria-label={`${additions} additions and ${deletions} deletions`}
                  >
                    {Array.from({ length: additionBlockCount }, (_, index) => (
                      <span
                        key={`addition-${index}`}
                        className={styles.additionsBlock}
                      />
                    ))}
                    {Array.from({ length: deletionBlockCount }, (_, index) => (
                      <span
                        key={`deletion-${index}`}
                        className={styles.deletionsBlock}
                      />
                    ))}
                    {Array.from({ length: emptyBlockCount }, (_, index) => (
                      <span
                        key={`empty-${index}`}
                        className={styles.emptyChangeBlock}
                      />
                    ))}
                  </div>
                </div>
              )}
              {pullRequestStatus && (
                <Tag colorSet={statusColorSet}>{pullRequestStatus}</Tag>
              )}
            </div>
          </div>
          <div className={styles.pullRequestDetails}>
            {reviewersLabel && (
              <Text as="span" variant="marginal" semanticColor="textSubdued">
                {reviewersLabel}
              </Text>
            )}
            {pullRequest.labels.map(label => (
              <Tag key={label}>{emojify(label)}</Tag>
            ))}
          </div>
        </div>
      </div>
    </Box>
  );
};

const CodeReviewXirpButton = ({
  codeReview,
  onLaunch,
}: {
  codeReview: CodeReviewInputRequest;
  onLaunch: () => void;
}) => {
  const { data } = usePullRequestQuery({
    variables: {
      repository: codeReview.repository,
      number: codeReview.pullRequestNumber,
    },
    fetchPolicy: 'cache-only',
  });
  const pullRequest = getTypeOrNull(data?.pullRequest);

  if (!pullRequest?.head?.ref) {
    return null;
  }

  return (
    <CodeReviewXirpForSpotifyAction
      pullRequestBranch={pullRequest.head.ref}
      pullRequestNumber={pullRequest.number ?? codeReview.pullRequestNumber}
      pullRequestUrl={pullRequest.htmlUrl ?? codeReview.uri}
      repositoryName={codeReview.repository}
      onLaunch={onLaunch}
    />
  );
};

const CodeReviewXirpLaunch = ({
  codeReview,
}: {
  codeReview: CodeReviewInputRequest;
}) => {
  const [hasAttemptedLaunch, setHasAttemptedLaunch] = useState(false);

  return (
    <Flex direction="column" alignItems="start" gap="tighter-2">
      <CodeReviewXirpButton
        codeReview={codeReview}
        onLaunch={() => setHasAttemptedLaunch(true)}
       data-track-source-anchor="41ef2ed5a7;code-review-xirp-launch-code-review-xirp-button"/>
      {hasAttemptedLaunch && (
        <CodingSessionLaunchFallback application="Xirp for Spotify" />
      )}
    </Flex>
  );
};

type TaskInputActionsProps = {
  disabled: boolean;
  isMutating: boolean;
  onSubmit: (inputResponse: TaskInputResponseInput) => Promise<void>;
};

const TaskInputCommentForm = ({
  label,
  placeholder,
  comment,
  disabled,
  onCommentChange,
  children,
  footer,
}: {
  label: string;
  placeholder?: string;
  comment: string;
  disabled: boolean;
  onCommentChange: (comment: string) => void;
  children: ReactNode;
  footer?: ReactNode;
}) => {
  const commentFieldId = useId();

  return (
    <Flex direction="column" gap="base" marginBlockStart="tighter-2">
      <FormGroup
        label={label}
        labelFor={commentFieldId}
        className={styles.taskInputFormGroup}
      >
        <FormTextarea
          id={commentFieldId}
          placeholder={placeholder}
          value={comment}
          disabled={disabled}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
            onCommentChange(event.target.value);
          }}
         data-track-source-anchor="160871feca;task-input-comment-form-textarea"/>
      </FormGroup>
      <Flex direction="column" alignItems="start" gap="tighter-2">
        <Flex wrap="wrap" alignItems="center" gap="tighter">
          {children}
        </Flex>
        {footer}
      </Flex>
    </Flex>
  );
};

const TaskInputApprovalActions = ({
  disabled,
  isMutating,
  onSubmit,
}: TaskInputActionsProps) => {
  const [comment, setComment] = useState('');
  const isDisabled = disabled || isMutating;

  const submit = async (approved: boolean) => {
    await onSubmit(buildReviewInputResponse('approval', approved, comment));
    setComment('');
  };

  return (
    <TaskInputCommentForm
      label="Comment (optional)"
      placeholder="Add a comment with your decision"
      comment={comment}
      disabled={isDisabled}
      onCommentChange={setComment}
    >
      <Button
        type="button"
        variant="primary"
        disabled={isDisabled}
        isLoading={isMutating}
        loadingAriaLabel="Approving task input"
        data-track-id="button.approve-task-input"
        data-track-action="update"
        onClick={() => {
          void submit(true);
        }}
       data-track-source-anchor="d9bbac463a;;high">
        Approve
      </Button>
      <Button
        type="button"
        variant="secondary"
        disabled={isDisabled}
        data-track-id="button.reject-task-input"
        data-track-action="update"
        onClick={() => {
          void submit(false);
        }}
       data-track-source-anchor="6ce6fe92a4;;high">
        Reject
      </Button>
    </TaskInputCommentForm>
  );
};

const TaskInputCodeReviewActions = ({
  codeReview,
  disabled,
  isMutating,
  onSubmit,
}: TaskInputActionsProps & { codeReview: CodeReviewInputRequest }) => {
  const [comment, setComment] = useState('');
  const [hasAttemptedLaunch, setHasAttemptedLaunch] = useState(false);
  const isDisabled = disabled || isMutating;

  const submit = async () => {
    await onSubmit(buildReviewInputResponse('codeReview', false, comment));
    setComment('');
  };

  return (
    <TaskInputCommentForm
      label="Tell the agent what to change"
      comment={comment}
      footer={
        hasAttemptedLaunch && (
          <CodingSessionLaunchFallback application="Xirp for Spotify" />
        )
      }
      disabled={isDisabled}
      onCommentChange={setComment}
    >
      <Button
        type="button"
        variant="primary"
        disabled={isDisabled || !comment.trim()}
        isLoading={isMutating}
        loadingAriaLabel="Requesting changes"
        data-track-id="button.request-code-review-changes"
        data-track-action="update"
        onClick={() => {
          void submit();
        }}
       data-track-source-anchor="f4e8c95a1b">
        Request changes
        <RiArrowRightLine size={16} aria-hidden />
      </Button>
      <CodeReviewXirpButton
        codeReview={codeReview}
        onLaunch={() => setHasAttemptedLaunch(true)}
       data-track-source-anchor="2ac6ca1be2;task-input-code-review-actions-code-review-xirp"/>
      <Text variant="marginal" semanticColor="textSubdued">
        Merge the pull request to close this issue.
      </Text>
    </TaskInputCommentForm>
  );
};

type DirectionSelection = number | typeof OTHER_DIRECTION_OPTION;

const TaskInputDirectionActions = ({
  options,
  disabled,
  isMutating,
  onSubmit,
}: TaskInputActionsProps & { options: readonly string[] }) => {
  const id = useId();
  const customDirectionFieldId = `${id}-custom`;
  const [selected, setSelected] = useState<DirectionSelection | null>(null);
  const [customDirection, setCustomDirection] = useState('');
  const isDisabled = disabled || isMutating;
  let choice: string | undefined;
  if (selected === OTHER_DIRECTION_OPTION) {
    choice = customDirection.trim();
  } else if (selected !== null) {
    choice = options[selected];
  }
  const choices: { key: string; label: string; value: DirectionSelection }[] = [
    ...options.map((label, index) => ({
      key: `direction-${index}`,
      label,
      value: index,
    })),
    {
      key: OTHER_DIRECTION_OPTION,
      label: 'Other direction',
      value: OTHER_DIRECTION_OPTION,
    },
  ];

  const submit = async () => {
    if (!choice) {
      return;
    }

    await onSubmit({ direction: { choice } });
    setSelected(null);
    setCustomDirection('');
  };

  return (
    <Flex direction="column" gap="base" marginBlockStart="tighter-2">
      <FormGroup
        withFieldset
        label="Direction options"
        hideLabel
        className={styles.taskInputFormGroup}
      >
        <Flex
          direction="column"
          variant="bordered"
          borderRadius="larger"
          overflow="hidden"
        >
          {choices.map(option => {
            const checked = selected === option.value;
            return (
              <div
                key={option.key}
                className={classNames(styles.taskInputDirectionOption, {
                  [styles.taskInputDirectionOptionSelected]: checked,
                })}
              >
                <FormRadio
                  id={`${id}-${option.key}`}
                  name={`${id}-direction`}
                  value={option.key}
                  checked={checked}
                  disabled={isDisabled}
                  semanticColor="essentialAnnouncement"
                  onChange={() => setSelected(option.value)}
                 data-track-source-anchor="e4b9cafa37;task-input-direction-actions-form-radio">
                  {option.label}
                </FormRadio>
              </div>
            );
          })}
        </Flex>
      </FormGroup>
      {selected === OTHER_DIRECTION_OPTION && (
        <FormGroup
          label="Describe how to proceed"
          labelFor={customDirectionFieldId}
          className={styles.taskInputFormGroup}
        >
          <FormTextarea
            id={customDirectionFieldId}
            value={customDirection}
            disabled={isDisabled}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
              setCustomDirection(event.target.value);
            }}
           data-track-source-anchor="b647c7a402;task-input-direction-actions-form-textarea"/>
        </FormGroup>
      )}
      <Flex wrap="wrap" alignItems="center" gap="tighter">
        <Button
          type="button"
          variant="primary"
          disabled={isDisabled || !choice}
          isLoading={isMutating}
          loadingAriaLabel="Submitting direction"
          data-track-id="button.submit-task-direction"
          data-track-action="update"
          onClick={() => {
            void submit();
          }}
         data-track-source-anchor="283172173d">
          Continue
          <RiArrowRightLine size={16} aria-hidden />
        </Button>
      </Flex>
    </Flex>
  );
};

export type TaskInputRequestPanelProps = {
  disabled?: boolean;
  issueName: string;
  taskName: string;
  inputRequest: TaskInputRequest;
  inputResponse?: TaskInputResponse | null;
  onProvideInput?: (inputResponse: TaskInputResponseInput) => Promise<void>;
};

export const TaskInputRequestContent = ({
  disabled = false,
  issueName,
  taskName,
  inputRequest,
  inputResponse,
  onProvideInput,
}: TaskInputRequestPanelProps) => {
  const { provideIssueTaskInput, isMutating } =
    useProvideIssueTaskInput(issueName);
  const inputResponseLabel = inputResponse
    ? getTaskInputResponseLabel(inputResponse)
    : undefined;
  const hasInputResponse = Boolean(inputResponse);

  const handleProvideInputResponse = async (
    taskInputResponse: TaskInputResponseInput,
  ): Promise<void> => {
    if (onProvideInput) {
      await onProvideInput(taskInputResponse);
      return;
    }

    await provideIssueTaskInput(taskName, taskInputResponse);
  };

  return (
    <Flex direction="column" gap="tighter">
      {inputRequest.approval?.prompt && (
        <div className={styles.taskInputPrompt}>
          <MarkdownContent size="small">
            {inputRequest.approval.prompt}
          </MarkdownContent>
        </div>
      )}
      {inputRequest.direction?.prompt && (
        <div className={styles.taskInputPrompt}>
          <MarkdownContent size="small">
            {inputRequest.direction.prompt}
          </MarkdownContent>
        </div>
      )}
      {inputRequest.codeReview?.prompt && (
        <div className={styles.taskInputPrompt}>
          <MarkdownContent size="small">
            {inputRequest.codeReview.prompt}
          </MarkdownContent>
        </div>
      )}
      {inputRequest.codeReview && (
        <PullRequestPreview codeReview={inputRequest.codeReview} />
      )}
      {inputRequest.codeReview &&
        (hasInputResponse ? (
          <CodeReviewXirpLaunch codeReview={inputRequest.codeReview} />
        ) : (
          <TaskInputCodeReviewActions
            codeReview={inputRequest.codeReview}
            disabled={disabled}
            isMutating={isMutating}
            onSubmit={handleProvideInputResponse}
           data-track-source-anchor="78058daa02;task-input-request-content-task-input-code"/>
        ))}
      {inputRequest.direction && !hasInputResponse && (
        <TaskInputDirectionActions
          options={inputRequest.direction.options ?? []}
          disabled={disabled}
          isMutating={isMutating}
          onSubmit={handleProvideInputResponse}
         data-track-source-anchor="4c014d11bc;task-input-request-content-task-input-direction"/>
      )}
      {inputRequest.approval && !hasInputResponse && (
        <TaskInputApprovalActions
          disabled={disabled}
          isMutating={isMutating}
          onSubmit={handleProvideInputResponse}
         data-track-source-anchor="890bbf4c58;task-input-request-content-task-input-approval"/>
      )}
      {inputResponseLabel && (
        <Text as="p" variant="bodySmall" className={styles.taskInputResponse}>
          Response: {inputResponseLabel}
        </Text>
      )}
    </Flex>
  );
};

export const TaskInputRequestPanel = (props: TaskInputRequestPanelProps) => (
  <Flex direction="column" gap="tighter" variant="elevated" padding="base">
    <Text as="span" variant="marginalBold" semanticColor="textSubdued">
      Input requested
    </Text>
    <TaskInputRequestContent {...props} />
  </Flex>
);

const TaskCardContent = ({
  canEdit,
  isHighlighted = false,
  task,
  issueName,
}: {
  canEdit: boolean;
  isHighlighted?: boolean;
  task: TaskFragment;
  issueName: string;
}) => {
  const taskPlan = getTypeOrNull(task.plan);
  const taskMarkdown = taskPlan?.markdown?.trim();
  const updatedAt = DateUtils.xAgoShort(new Date(task.updateTime));

  return (
    <ListRow
      variant="naked"
      size="large"
      height="auto"
      alignItems="start"
      className={classNames(styles.taskRow, {
        [styles.taskRowHighlighted]: isHighlighted,
      })}
    >
      <div className={styles.taskStateIconCell}>
        <TaskStateIcon state={task.state} />
      </div>
      <div className={styles.taskContent}>
        <div className={styles.taskHeader}>
          <Text
            as="h4"
            variant="bodyMediumBold"
            lineClamp={1}
            className={styles.taskTitle}
          >
            {task.displayName}
          </Text>
          <Text
            as="span"
            variant="marginal"
            semanticColor="textSubdued"
            className={styles.taskUpdatedAt}
          >
            {updatedAt}
          </Text>
          {taskMarkdown && (
            <div className={styles.taskHeaderCopyButtonWrapper}>
              <Copy
                value={taskMarkdown}
                label="Copy plan as markdown"
                variant="compact"
              />
            </div>
          )}
        </div>
        {taskMarkdown && (
          <ExpandableContent className={styles.taskPlan}>
            <div className={styles.taskPlanMarkdown}>
              <MarkdownContent components={taskPlanMarkdownComponents}>
                {taskMarkdown}
              </MarkdownContent>
            </div>
          </ExpandableContent>
        )}
        {task.inputRequest && (
          <TaskInputRequestPanel
            issueName={issueName}
            taskName={task.name}
            inputRequest={task.inputRequest}
            inputResponse={task.inputResponse}
            disabled={!canEdit}
          />
        )}
        {task.blocked && (
          <div className={styles.taskMeta}>
            <Tag className={styles.blockedTag}>Blocked</Tag>
          </div>
        )}
        {task.state === TaskState.InProgress && (
          <TaskLatestChatEvent issueName={issueName} />
        )}
      </div>
    </ListRow>
  );
};

export const TaskCardFallback = ({
  canEdit,
  isHighlighted,
  task,
  issueName,
}: TaskCardFallbackProps) => (
  <TaskCardContent
    canEdit={canEdit}
    isHighlighted={isHighlighted}
    task={task}
    issueName={issueName}
  />
);

export const TaskCard = ({
  canEdit,
  isHighlighted,
  taskName,
  issueName,
}: TaskCardProps) => {
  const { task } = useTaskSuspense({
    name: taskName,
    fetchPolicy: 'cache-first',
  });

  return (
    <TaskCardContent
      canEdit={canEdit}
      isHighlighted={isHighlighted}
      task={task}
      issueName={issueName}
    />
  );
};
